import type { ComponentProps, PointerEvent } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The HyperTek prototype's dropdown menu (h-prototype `components/ui/dropdown-menu.tsx`), class for class,
 * with two product deltas:
 *
 * - NON-MODAL ONLY. Radix menus default to `modal`, which locks scrolling through react-remove-scroll — a
 *   runtime `<style>` element that production's `style-src 'self'` refuses. `modal` is removed from the
 *   props and pinned to `false`; outside clicks and Escape still dismiss the menu.
 * - `min-h-11` on an item: the 44 px touch floor under a thumb; 36 px under a fine pointer.
 * - A checked checkbox or radio item takes the selected-item look: a primary tint and primary text, beside its
 *   check or dot.
 * - An item's mouse hover never takes the focus from outside its menu (`keepFocusOutside`).
 */
function DropdownMenu(props: Omit<ComponentProps<typeof DropdownMenuPrimitive.Root>, 'modal'>) {
  return <DropdownMenuPrimitive.Root {...props} modal={false} />;
}

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuGroup = DropdownMenuPrimitive.Group;
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
const DropdownMenuSub = DropdownMenuPrimitive.Sub;
const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

function DropdownMenuContent({ className, sideOffset = 4, ...props }: ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        className={cn(
          // z-[70], like a popover: above a dialog (z-[60]), so a menu opened inside the phone's sheet is seen.
          'z-[70] min-w-[8rem] overflow-hidden rounded-lg border border-[hsl(var(--h-border))] bg-[hsl(var(--h-card))] p-1 text-[hsl(var(--h-card-foreground))] shadow-lg',
          // Grows from its trigger's side and fades out (styles/motion.css `.h-pop`); Radix waits for the exit.
          'h-pop',
          className,
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

/**
 * Radix moves the focus with the mouse: onto an item it moves over, and back to the menu when the mouse leaves one.
 * When an item opens a dialog, the dialog's scrim covers the item, the browser sends it a `pointerleave`, and Radix
 * took the focus from the open dialog into the menu, which then left the page and dropped it on `<body>`. A hover
 * moves the focus only while the focus is in that menu, as it is for as long as the menu is open and in use.
 */
function keepFocusOutside<E extends PointerEvent<HTMLElement>>(own?: (event: E) => void) {
  return (event: E) => {
    own?.(event);
    const menu = event.currentTarget.closest('[role="menu"]');
    if (menu === null || !menu.contains(document.activeElement)) event.preventDefault();
  };
}

function DropdownMenuItem({
  className, inset, onPointerMove, onPointerLeave, ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Item> & { inset?: boolean }) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      onPointerMove={keepFocusOutside(onPointerMove)}
      onPointerLeave={keepFocusOutside(onPointerLeave)}
      className={cn(
        'relative flex min-h-11 pointer-fine:min-h-9 cursor-pointer select-none items-center rounded-md px-3 py-2 text-sm outline-none transition-colors focus:bg-[hsl(var(--h-muted))] focus:text-[hsl(var(--h-card-foreground))] data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        inset && 'pl-8',
        className,
      )}
      {...props}
    />
  );
}

// A checkbox or radio item: the item's own box, with room on the left for its check or dot.
const CHOICE_ITEM = 'relative flex min-h-11 pointer-fine:min-h-9 cursor-pointer select-none items-center gap-2 rounded-md py-2 pl-8 pr-3 text-sm outline-none transition-colors focus:bg-[hsl(var(--h-muted))] focus:text-[hsl(var(--h-card-foreground))] data-[state=checked]:bg-[hsl(var(--h-primary)/0.1)] data-[state=checked]:text-[hsl(var(--h-primary))] data-[state=checked]:focus:bg-[hsl(var(--h-primary)/0.14)] data-[state=checked]:focus:text-[hsl(var(--h-primary))] data-[disabled]:pointer-events-none data-[disabled]:opacity-50';

function DropdownMenuCheckboxItem({
  className, children, onPointerMove, onPointerLeave, ...props
}: ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(CHOICE_ITEM, className)}
      onPointerMove={keepFocusOutside(onPointerMove)}
      onPointerLeave={keepFocusOutside(onPointerLeave)}
      {...props}
    >
      <span className="pointer-events-none absolute left-2.5 flex size-4 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Check aria-hidden="true" className="size-4" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
}

function DropdownMenuRadioItem({
  className, children, onPointerMove, onPointerLeave, ...props
}: ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) {
  return (
    <DropdownMenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={cn(CHOICE_ITEM, className)}
      onPointerMove={keepFocusOutside(onPointerMove)}
      onPointerLeave={keepFocusOutside(onPointerLeave)}
      {...props}
    >
      <span className="pointer-events-none absolute left-2.5 flex size-4 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Circle aria-hidden="true" className="size-2 fill-current" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
}

function DropdownMenuLabel({
  className, inset, ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Label> & { inset?: boolean }) {
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      className={cn('px-3 py-2 text-sm font-semibold text-[hsl(var(--h-card-foreground))]', inset && 'pl-8', className)}
      {...props}
    />
  );
}

function DropdownMenuSeparator({ className, ...props }: ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn('-mx-1 my-1 h-px bg-[hsl(var(--h-border))]', className)}
      {...props}
    />
  );
}

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuRadioGroup,
};
